import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, AlertTriangle, ShieldAlert, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

/**
 * 5-fach Bestätigungs-Dialog zum Löschen einer Firma.
 * Schritt 1: PDF-Archiv herunterladen (Pflicht)
 * Schritt 2-5: 4 Warnungs-Checkboxen
 * Final: Stichwort "LÖSCHEN" eintippen
 */
export default function CompanyDeleteDialog({ open, onOpenChange, company, onDeleted }) {
  const [archiveDownloaded, setArchiveDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);
  const [c4, setC4] = useState(false);
  const [c5, setC5] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const reset = () => {
    setArchiveDownloaded(false);
    setC2(false); setC3(false); setC4(false); setC5(false);
    setConfirmText("");
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const downloadArchive = async () => {
    if (!company) return;
    setDownloading(true);
    try {
      const r = await apiClient.post(
        `/admin/companies/${company.id}/export-archive`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m?.[1] || `archiv_${company.name}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setArchiveDownloaded(true);
      toast.success("Archiv heruntergeladen");
    } catch (e) {
      toast.error("Archiv-Download fehlgeschlagen");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!company) return;
    setDeleting(true);
    try {
      const r = await apiClient.delete(`/admin/companies/${company.id}`, {
        headers: { "X-Confirm-Count": "5" },
        data: { confirmation_text: confirmText, archive_downloaded: archiveDownloaded },
      });
      toast.success(`Firma "${r.data.company_name}" gelöscht`);
      reset();
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  };

  const allChecked = c2 && c3 && c4 && c5;
  const canDelete = archiveDownloaded && allChecked && confirmText === "LÖSCHEN";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="company-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="h-5 w-5" />
            Firma löschen — {company?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <p className="font-medium flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Unwiderruflich — bitte 5-fache Bestätigung
            </p>
            <p className="text-xs mt-1 text-amber-800">
              Daten werden zusätzlich automatisch im Cloud-Backup gesichert (Infomaniak).
              Trotzdem solltest du das PDF-Archiv für deine eigenen Akten herunterladen
              (GeBüV 10-Jahre-Pflicht).
            </p>
          </div>

          {/* Schritt 1: Archiv-Download */}
          <div className="rounded-md border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-slate-900 text-sm">Schritt 1 / 5 — Archiv exportieren</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Lädt alle Stammdaten, Belege, Rechnungen, Journale, Lohnläufe & Anlagen als
                  ZIP mit mehreren PDFs herunter.
                </p>
              </div>
              <Button
                size="sm"
                onClick={downloadArchive}
                disabled={downloading || archiveDownloaded}
                data-testid="company-delete-download-archive"
              >
                <Download className={`h-3.5 w-3.5 mr-1.5 ${downloading ? "animate-pulse" : ""}`} />
                {archiveDownloaded ? "Geladen ✓" : downloading ? "Lädt…" : "ZIP herunterladen"}
              </Button>
            </div>
          </div>

          {/* Schritte 2-5: Checkboxen */}
          <div className="space-y-3">
            {[
              { id: "c2", val: c2, set: setC2, n: 2,
                t: "Ich verstehe, dass alle Belege, Quittungen und PDFs unwiderruflich gelöscht werden." },
              { id: "c3", val: c3, set: setC3, n: 3,
                t: "Ich verstehe, dass alle Rechnungen (Debitoren & Kreditoren) und das Hauptjournal gelöscht werden." },
              { id: "c4", val: c4, set: setC4, n: 4,
                t: "Ich verstehe, dass alle Lohnläufe, Mitarbeitenden-Daten und Anlagevermögen gelöscht werden." },
              { id: "c5", val: c5, set: setC5, n: 5,
                t: "Ich habe das ZIP-Archiv gesichert und der Owner hat zugestimmt." },
            ].map((c) => (
              <label key={c.id} className="flex items-start gap-3 cursor-pointer" data-testid={`company-delete-check-${c.id}`}>
                <Checkbox
                  checked={c.val}
                  onCheckedChange={c.set}
                  disabled={!archiveDownloaded}
                  className="mt-0.5"
                  data-testid={`company-delete-checkbox-${c.id}`}
                />
                <div>
                  <p className="text-xs font-medium text-slate-700">Schritt {c.n} / 5</p>
                  <p className="text-sm text-slate-800">{c.t}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Final: Stichwort */}
          <div className="rounded-md border border-rose-200 bg-rose-50/40 p-4">
            <Label htmlFor="confirm-text" className="text-sm font-semibold text-rose-900">
              Tippe zur Bestätigung: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-rose-300">LÖSCHEN</span>
            </Label>
            <Input
              id="confirm-text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={!allChecked}
              className="mt-2 font-mono"
              placeholder="LÖSCHEN"
              data-testid="company-delete-confirm-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="company-delete-cancel">
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            disabled={!canDelete || deleting}
            onClick={handleDelete}
            data-testid="company-delete-confirm"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {deleting ? "Lösche…" : "Firma endgültig löschen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
